/**
 * Which texel is the mirror of which, derived from the body rather than declared.
 *
 * Mirroring a Roblox garment is not a horizontal flip of the template. The 18
 * regions have to be remapped, and the limb strips are the trap: they ROTATE
 * rather than reflect.
 *
 *     right limb, left to right:   left · back · right · front
 *     left  limb, left to right:   front · left · back · right
 *
 * So a hand-written pair table has to encode the strip rotation, a flip per
 * region, the torso's left/right swap and the two caps — four chances to be
 * wrong, none of which shows up until it renders on a body. That is exactly the
 * mistake the flip-a-copy-in-Photoshop workflow makes, and shipping our own
 * version of it would be a poor way to beat it. `template-regions.json` carries
 * such a table and marks itself `"verified": false`.
 *
 * The position map already knows where every texel lives in 3D, and the body is
 * symmetric — measured at 0.00% area drift per mirrored region once the
 * projection ranked chamfers by face role. So the mirror of a texel is just:
 *
 *     the texel nearest to reflect(position(texel))
 *
 * Region swaps, strip rotation, per-face flips and cap handling all fall out as
 * consequences instead of being written down.
 *
 * "The body is symmetric" turned out to need care. Two of the shipped rigs are
 * not, and not in the same way: r15-woman's legs sit off centre AND are
 * different widths from each other, 0.901 studs against 0.801. So the mirror is
 * measured per part pair rather than assumed about x = 0 — reflect through the
 * plane the pair straddles, then fit the counterpart's own extent. On a rig that
 * IS symmetric both reduce to `2*axis - x` and the result is byte-identical.
 *
 * Coverage on every shipped body and both garments is now 100% of mapped
 * texels, with no paintable texel left unmirrored.
 *
 * See docs/decisions/mirror-and-editor-tools.md.
 */
import { PointGrid } from "./point-grid";
import { type PositionMap, type TexelRect, texelScale } from "./position-map";
import { GUTTER_PX as GUTTER } from "./regions";

/**
 * How much of a region's geometry a transform must reproduce to be believed.
 *
 * 0.90, not the 0.97 it was. That figure was a guess made to stop the anthro
 * rig's curved torso adopting a linear mirror, and it rejected transforms
 * scoring 96.2 to 96.8 — not near-misses, but the right answer measured against
 * geometry that is a texel out along a rounded edge. Rejecting them left those
 * regions with NO mirror: 2,652 unmirrored texels on the R15 shirt and 36,006
 * on the anthro one, a quarter of its paintable area, arriving as strokes full
 * of gaps.
 *
 * Nothing sits between. A genuinely wrong transform is one of eight dihedrals
 * and scores near zero, not in the nineties, so there is no band here for a bad
 * fit to slip through.
 */
const FIT_MIN = 0.9;

export interface MirrorMap {
  width: number;
  height: number;
  /** Mirrored texel index, or -1 where this texel has no partner. */
  partner: Int32Array;
  /** Mapped texels that found a partner, over mapped texels. */
  pairedFraction: number;
  /** The distance below which two points were treated as the same place. */
  tolerance: number;
  /** Per-region transforms read off the geometry. Empty if `regions` was not given. */
  fits: RegionFit[];
  /**
   * Which half of the body a texel is on, in the **character's own frame**:
   * `1` its left, `-1` its right, `0` on the plane or not mapped at all.
   *
   * Live symmetry never needs this — it mirrors whatever was just painted, in
   * whichever direction. A one-shot "make this symmetric" does, because it has
   * to overwrite one half with the other and somebody has to say which.
   *
   * In the character's frame rather than the viewer's, and derived rather than
   * assumed: which sign of x is a body's left is a fact about the export, not
   * something to guess from the axis convention. See `sidesOf`.
   */
  side: Int8Array;
}

export interface MirrorOptions {
  /**
   * How near the reflection a candidate has to be, in studs. Defaults to 1.5
   * texels' worth of surface, which is the scale at which "the same place on
   * the other side" stops being ambiguous.
   */
  tolerance?: number;
  /**
   * Plane to reflect through, on the x axis. Measured from the body when
   * omitted, which is almost always what you want — see `symmetryPlane`.
   */
  axis?: number;
  /**
   * Region rects. Supplying them lets the map extend a fitted transform over
   * each region's sealed ring, which geometry alone cannot pair. Without them
   * the map is still correct, just ~11% sparser at the seams.
   */
  regions?: Record<string, TexelRect>;
  /**
   * A second, COARSER set of rects, fitted after `regions` and applied only
   * where the first pass left no partner.
   *
   * Segmented rigs need both. A limb face on an R15 body is sealed as three
   * stacked bands, and fitting per band is finer and usually better — but a
   * band whose transform scores below `FIT_MIN` is skipped entirely, and a
   * third of a face then falls back to geometric pairs alone. Measured on the
   * maps the viewer actually builds: 4,420 unmirrored texels on the R15 shirt,
   * 3,895 on the anthro, where the whole-face fit covers them.
   *
   * Neither granularity dominates, so both are used: the finer one first
   * because it follows a tapering limb more closely, the coarser one to fill
   * what it declined. Both are read off the geometry, so this adds no guesses,
   * only a second chance.
   */
  coarseRegions?: Record<string, TexelRect>;
}

/** LeftArm <-> RightArm, Torso -> Torso. Part names are the rig's own. */
export function mirrorPartName(name: string): string {
  return name.replace(/^(Left|Right)/, (m) => (m === "Left" ? "Right" : "Left"));
}

/**
 * Label each texel's half of the body, in the character's own frame.
 *
 * The sign of x alone does not say which half is a *left* half. That depends on
 * which way the rig faces, which is a property of how the body was exported —
 * and the one place it is written down unambiguously is the part names, because
 * `LeftUpperArm` is only ever on the character's left. So the sign convention is
 * measured off the parts that name themselves rather than assumed from the axis:
 * take the mean x of every `Left`-named part, and whichever side of the plane it
 * lands on is left for this body.
 *
 * A body with no `Left`-named part gets all zeroes, which reads downstream as
 * "this map has no halves" and disables the commands that need them. That is the
 * honest outcome — a guess here would silently overwrite the wrong half of
 * somebody's finished artwork, and there is no undo for surprise.
 */
function sidesOf(
  map: PositionMap,
  idx: Int32Array,
  xs: Float32Array,
  owner: Int32Array,
  n: number,
  axis: number,
): Int8Array {
  const side = new Int8Array(map.width * map.height);
  const isLeft = map.parts.map((name) => /^Left/.test(name));

  let sum = 0;
  let seen = 0;
  for (let k = 0; k < n; k++) {
    if (!isLeft[owner[k] as number]) continue;
    sum += (xs[k] as number) - axis;
    seen++;
  }
  if (seen === 0 || sum === 0) return side;

  const leftSign = sum > 0 ? 1 : -1;
  for (let k = 0; k < n; k++) {
    const d = (xs[k] as number) - axis;
    if (d === 0) continue;
    side[idx[k] as number] = d > 0 ? leftSign : -leftSign;
  }
  return side;
}

/**
 * Where a body's plane of symmetry actually is, on the x axis.
 *
 * It is not always zero, and assuming it was cost this map an entire garment.
 * r15-woman's legs are built off centre — `LeftUpperLeg` sits at x = -0.449 and
 * `RightUpperLeg` at +0.401, a 0.048-stud shift of the whole assembly.
 * Reflecting through zero therefore lands every left-leg texel 0.048 from its
 * partner, against a tolerance of 0.023, and NOTHING pairs: 32,928 unmirrored
 * texels, whole regions of 8,192 with no partner at all, and 27 of 51 region
 * fits scoring under 6%.
 *
 * None of which is a defect in the pairing. It is a fact about the export, and
 * the same answer applies as everywhere else here: measure it rather than
 * assume it. Each `Left`-named part and its `Right` counterpart straddle the
 * plane, so the midpoint of their centres is a sample of where it lies, and the
 * mean over every such pair is the plane.
 *
 * **Per part pair, not per body**, and that distinction is the whole of it. A
 * single measured plane is WORSE than assuming zero: r15-woman's torso is
 * symmetric about 0 while only its legs are offset, so any global compromise
 * breaks whichever it is not — measured, the body-wide plane took the shirt from
 * 1,522 unmirrored texels to 67,192.
 *
 * **And a scale, not only a reflection.** r15-woman's legs are not the same
 * size as each other: the left spans 0.901 studs across and the right 0.801, a
 * mesh that is genuinely asymmetric rather than merely off centre. No plane
 * reflects one onto the other, which is why 32,928 of that garment's texels had
 * no mirror at all. Both legs are painted through the SAME template region, so
 * the artwork is the same size in template space either way — the mapping the
 * designer means is region onto region, and fitting the width is what expresses
 * that.
 *
 * Falls back to reflecting through a part's own centre when it has no
 * counterpart, which is right for a torso and the only honest answer for
 * anything else.
 */
export interface PartMirror {
  /** Midpoint of this part's x extent. */
  centre: number;
  /** Width of this part's x extent. */
  width: number;
  /** Index of the part this one mirrors onto, or -1. */
  onto: number;
}

export function partMirrors(map: PositionMap): PartMirror[] {
  /*
    The midpoint of each part's x EXTENT, not the centroid of its texels.

    A centroid is weighted by texel density, and density is not uniform: a face
    projected edge-on to its axis gets far fewer texels than one facing it, so a
    part's texel centroid sits wherever its biggest faces are. Measured, that
    bias was enough to move the plane off a part that is genuinely centred and
    put 13,756 holes into a garment that had none.

    The extent's midpoint has no such weighting — it depends only on where the
    part begins and ends.
  */
  const lo = map.parts.map(() => Number.POSITIVE_INFINITY);
  const hi = map.parts.map(() => Number.NEGATIVE_INFINITY);
  for (let i = 0; i < map.width * map.height; i++) {
    if (map.data[i * 4 + 3] !== 1) continue;
    const pi = map.part[i] as number;
    if (pi < 0) continue;
    const x = map.data[i * 4] as number;
    if (x < (lo[pi] as number)) lo[pi] = x;
    if (x > (hi[pi] as number)) hi[pi] = x;
  }
  const centreOf = (i: number) => {
    const a = lo[i] as number;
    const b = hi[i] as number;
    return Number.isFinite(a) && Number.isFinite(b) ? (a + b) / 2 : null;
  };

  return map.parts.map((name, i) => {
    const j = map.parts.indexOf(mirrorPartName(name));
    const a = centreOf(i);
    const width = Number.isFinite(hi[i] as number) ? (hi[i] as number) - (lo[i] as number) : 0;
    return { centre: a ?? 0, width, onto: j };
  });
}

/** The body's own plane of symmetry: the mean over every part pair's. */
function planeOf(mirrors: PartMirror[]): number {
  let total = 0;
  let pairs = 0;
  mirrors.forEach((m, i) => {
    if (m.onto < 0 || m.onto === i) return;
    const other = mirrors[m.onto];
    if (!other) return;
    total += (m.centre + other.centre) / 2;
    pairs++;
  });
  return pairs === 0 ? 0 : total / pairs;
}

export function createMirrorMap(map: PositionMap, opts: MirrorOptions = {}): MirrorMap {
  const { width, height } = map;
  /*
    Per part, unless the caller insists on one plane for everything.

    `opts.axis` still overrides, because a test that wants to reflect through a
    known plane should be able to say so — but nothing in the app passes it, and
    the measured planes are what make an off-centre rig work.
  */
  const mirrors = partMirrors(map);
  /**
   * Where a texel of `part` lands when mirrored, on the x axis.
   *
   * Reflection about the pair's shared plane, then a scale to the counterpart's
   * width. Both reduce to the plain `2*axis - x` on a symmetric body, so the
   * blocky rigs are unaffected — measured byte-identical.
   */
  const reflectX = (part: number, x: number): number => {
    if (opts.axis !== undefined) return 2 * opts.axis - x;
    const m = mirrors[part];
    if (!m) return -x;
    const other = m.onto >= 0 ? mirrors[m.onto] : undefined;
    if (!other || m.width <= 0 || other.width <= 0) return 2 * m.centre - x;
    return other.centre - (x - m.centre) * (other.width / m.width);
  };
  const count = width * height;

  // The part a texel must land on to be its mirror. A texel on the left arm can
  // only pair with one on the right arm, whatever else happens to be nearby.
  const mirroredPart = map.parts.map((name) => map.parts.indexOf(mirrorPartName(name)));

  // Gather the mapped texels once, into flat arrays the grid can scan without
  // chasing objects.
  const idx = new Int32Array(count);
  const xs = new Float32Array(count);
  const ys = new Float32Array(count);
  const zs = new Float32Array(count);
  const owner = new Int32Array(count);
  let n = 0;
  for (let i = 0; i < count; i++) {
    if (map.data[i * 4 + 3] !== 1) continue;
    idx[n] = i;
    xs[n] = map.data[i * 4] as number;
    ys[n] = map.data[i * 4 + 1] as number;
    zs[n] = map.data[i * 4 + 2] as number;
    owner[n] = map.part[i] as number;
    n++;
  }

  const tolerance = opts.tolerance ?? texelScale(map) * 1.5;
  const partner = new Int32Array(count).fill(-1);
  if (n === 0) {
    const side = new Int8Array(count);
    return { width, height, partner, pairedFraction: 0, tolerance, fits: [], side };
  }

  const grid = new PointGrid(xs, ys, zs, owner, n, tolerance);

  for (let k = 0; k < n; k++) {
    // A part whose mirror is not in this garment's map cannot pair. Saying so
    // beats pairing it with whatever else is nearby.
    const want = mirroredPart[owner[k] as number] ?? -1;
    if (want < 0) continue;
    // Reflect through the plane and ask who lives there.
    const mx = reflectX(owner[k] as number, xs[k] as number);
    const hit = grid.nearest(mx, ys[k] as number, zs[k] as number, tolerance, want);
    if (hit >= 0) partner[idx[k] as number] = idx[hit] as number;
  }

  /*
    Keep only pairs that agree with each other.

    Nearest-neighbour is not automatically symmetric: where two texels sit
    almost on top of each other — parts interpenetrate at every R15 joint, and
    the seal copies positions outward — A can choose B while B chooses C. A
    mirror that is not an involution lets a stroke DRIFT under repeated
    application, which is the one failure a designer would notice and could not
    explain.

    Dropping the disagreements is the honest resolution: an unpaired texel
    simply does not mirror, where a wrong pair would paint somewhere else.
  */
  let paired = 0;
  for (let k = 0; k < n; k++) {
    const i = idx[k] as number;
    const j = partner[i] ?? -1;
    if (j < 0) continue;
    if (partner[j] !== i) partner[i] = -1;
    else paired++;
  }

  const fits = opts.regions ? fitRegions(partner, width, opts.regions) : [];
  // Second chance at a coarser granularity, gaps only. See `coarseRegions`.
  if (opts.coarseRegions) fits.push(...fitRegions(partner, width, opts.coarseRegions, true));
  if (fits.length > 0) {
    /*
      Recounted over MAPPED texels only, and that word is the whole fix.

      It used to sweep every texel in the canvas, while dividing by the mapped
      count — and the fits deliberately extend a transform over each region's
      sealed ring, which is outside the mapped set. So the numerator counted
      texels the denominator did not and `pairedFraction` came back at 106.9%.

      A fraction above one is not a rounding problem, it is a number that was
      never measuring what its name says, and it had been the headline health
      figure for this map since it was written.
    */
    paired = 0;
    for (let k = 0; k < n; k++) if ((partner[idx[k] as number] ?? -1) >= 0) paired++;
  }

  /*
    ONE plane for the whole body here, unlike the pairing above.

    `side` answers "which half of the body is this texel on", and a half is a
    property of the body, not of a part. Measuring it per part asks instead
    which half of the ARM a texel is on, which splits every limb down its own
    middle and labels half of the left arm as right — all five rigs failed at
    once, which is the good version of getting this wrong.
  */
  const bodyPlane = opts.axis ?? planeOf(mirrors);
  const side = sidesOf(map, idx, xs, owner, n, bodyPlane);

  return { width, height, partner, pairedFraction: paired / n, tolerance, fits, side };
}

/** How one region's texels land on another's. */
export interface RegionFit {
  from: string;
  to: string;
  flipX: boolean;
  flipY: boolean;
  swapXY: boolean;
  /** Share of geometrically-paired samples this transform reproduces. */
  agreement: number;
  /** Samples it was fitted from. */
  samples: number;
}

const DIHEDRAL: Omit<RegionFit, "from" | "to" | "agreement" | "samples">[] = [
  { flipX: false, flipY: false, swapXY: false },
  { flipX: true, flipY: false, swapXY: false },
  { flipX: false, flipY: true, swapXY: false },
  { flipX: true, flipY: true, swapXY: false },
  { flipX: false, flipY: false, swapXY: true },
  { flipX: true, flipY: false, swapXY: true },
  { flipX: false, flipY: true, swapXY: true },
  { flipX: true, flipY: true, swapXY: true },
];

/** Apply a dihedral transform from rect `a` to rect `b`. */
function place(
  fit: { flipX: boolean; flipY: boolean; swapXY: boolean },
  a: TexelRect,
  b: TexelRect,
  x: number,
  y: number,
): { x: number; y: number } {
  const u = x - a.x;
  const v = y - a.y;
  let p = fit.swapXY ? v : u;
  let q = fit.swapXY ? u : v;
  if (fit.flipX) p = b.w - 1 - p;
  if (fit.flipY) q = b.h - 1 - q;
  return { x: b.x + p, y: b.y + q };
}

/**
 * Turn the geometric pairing into a per-region transform, then apply it to
 * every texel of the region — including the ones geometry could not pair.
 *
 * The seal dilates a region's outer ring by COPYING positions outward, so five
 * or more texels can share one 3D point. Nearest-neighbour cannot tell them
 * apart, and the involution check then discards nearly all of them: measured,
 * the ring left 11% of the body unmirrored, concentrated in exactly the band
 * next to every seam — which is where someone painting a collar or a cuff is
 * working.
 *
 * The interior is unambiguous though, and a planar projection reflected through
 * a plane is still affine, so the interior is enough to identify which of the
 * eight dihedral transforms takes one region onto the other. Fitting it there
 * and extending it outward keeps the mapping DERIVED — nothing about the strip
 * rotation or the face flips is written down here, it is read off the body —
 * while covering the texels geometry alone cannot reach.
 *
 * The fit reports its own agreement, so a region whose transform is not
 * actually dihedral says so rather than silently applying a bad one.
 */
function fitRegions(
  partner: Int32Array,
  width: number,
  regions: Record<string, TexelRect>,
  fillOnly = false,
): RegionFit[] {
  const entries = Object.entries(regions);
  const at = (x: number, y: number) => partner[y * width + x] ?? -1;
  const out: RegionFit[] = [];

  /*
    Which region owns each texel, resolved once.

    This used to be a linear scan of the region list per paired texel — around
    18 lookups across 147k texels per body, and it dominated the build. Two test
    files building it for five bodies each put enough CPU-bound work into the
    worker pool to starve neighbouring files into their own timeouts, which
    showed up as six unrelated suites failing together and passing alone.
  */
  const ownerOf = new Int16Array(partner.length).fill(-1);
  entries.forEach(([, r], k) => {
    for (let y = r.y; y < r.y + r.h; y++) {
      const row = y * width;
      for (let x = r.x; x < r.x + r.w; x++) ownerOf[row + x] = k;
    }
  });

  for (const [from, a] of entries) {
    // Where do this region's paired texels land, and how often?
    const votes = new Map<string, number>();
    const samples: { x: number; y: number; tx: number; ty: number }[] = [];
    for (let y = a.y; y < a.y + a.h; y++) {
      for (let x = a.x; x < a.x + a.w; x++) {
        const j = at(x, y);
        if (j < 0) continue;
        const tx = j % width;
        const ty = Math.floor(j / width);
        const k = ownerOf[ty * width + tx] ?? -1;
        if (k < 0) continue;
        const name = (entries[k] as [string, TexelRect])[0];
        votes.set(name, (votes.get(name) ?? 0) + 1);
        samples.push({ x, y, tx, ty });
      }
    }
    if (samples.length < 64) continue;
    const to = [...votes.entries()].sort((p, q) => q[1] - p[1])[0]?.[0];
    if (!to) continue;
    const b = regions[to] as TexelRect;

    const keep = samples.filter(
      (s) => s.tx >= b.x && s.tx < b.x + b.w && s.ty >= b.y && s.ty < b.y + b.h,
    );
    let best: RegionFit | null = null;
    for (const d of DIHEDRAL) {
      // A transform that would send texels outside the target is not a candidate.
      if (d.swapXY ? a.w !== b.h || a.h !== b.w : a.w !== b.w || a.h !== b.h) continue;
      let agree = 0;
      for (const s of keep) {
        const p = place(d, a, b, s.x, s.y);
        /*
          Exact, with no slack.

          A texel of tolerance sounds harmless and is not. It let the fit be
          accepted on the anthro rig, whose torso is curved: a dihedral
          transform is a LINEAR mirror, the projection over a curve is not, and
          the two agree closely in the middle of a face and diverge toward its
          edge. Since the fit is then extended to exactly those edges, r15-man
          reported a mirror 0.2006 studs — thirteen texels — from the true
          reflection, on a body where geometry had been exact.

          Demanding the transform reproduce every measured pair on the nose
          means it is accepted only where the face really is a flat projection,
          which is the only place extending it is safe.
        */
        if (p.x === s.tx && p.y === s.ty) agree++;
      }
      const fit: RegionFit = {
        from,
        to,
        ...d,
        agreement: agree / keep.length,
        samples: keep.length,
      };
      if (!best || fit.agreement > best.agreement) best = fit;
    }
    if (best) out.push(best);
  }

  /*
    Fill the gaps, do not overwrite the measurements.

    An earlier version applied the fitted transform across the whole region and
    made things worse in two ways. It replaced exact geometric pairs with an
    idealised one, and on the shaped R6 woman torso that moved the interior by
    up to 0.041 studs — nearly three texels, where geometry had been exact. And
    because each direction was fitted independently, A's transform and B's were
    not guaranteed to be inverses, which broke the involution the whole map
    depends on.

    Measured pairs win. The fit only reaches texels nothing measured, which is
    precisely the sealed ring it was introduced for.

    A poor fit is left alone entirely: an unpaired texel does not mirror, which
    is honest, where a wrong pair paints somewhere the designer did not ask for.
  */
  /*
    Nearest region for every gutter texel, decided once by distance.

    Growing each region by the gutter and letting them race left the corners
    contested: two regions both reach a texel diagonally outside them, disagree
    about where it mirrors to, and the involution sweep drops BOTH. That was
    2,116 texels still unmirrored — the dashes at the face corners.

    Resolving by distance makes the owner a function of position alone, so it
    cannot depend on iteration order, and because a region pair's layout is
    mirror-symmetric the opposite texel gets the opposite owner. The two
    directions then agree by construction rather than by luck.
  */
  const fitIndex = new Map(entries.map(([name], k) => [name, k]));
  const gutterOwner = new Int16Array(partner.length).fill(-1);
  const bestDist = new Float32Array(partner.length).fill(Number.POSITIVE_INFINITY);
  for (let k = 0; k < entries.length; k++) {
    const r = (entries[k] as [string, TexelRect])[1];
    for (let y = r.y - GUTTER; y < r.y + r.h + GUTTER; y++) {
      for (let x = r.x - GUTTER; x < r.x + r.w + GUTTER; x++) {
        if (x < 0 || y < 0 || x >= width) continue;
        const i = y * width + x;
        if (i < 0 || i >= gutterOwner.length) continue;
        if ((ownerOf[i] ?? -1) >= 0) continue; // inside a region, already owned
        const dx = Math.max(r.x - x, 0, x - (r.x + r.w - 1));
        const dy = Math.max(r.y - y, 0, y - (r.y + r.h - 1));
        const d = dx * dx + dy * dy;
        if (d < (bestDist[i] ?? Number.POSITIVE_INFINITY)) {
          gutterOwner[i] = k;
          bestDist[i] = d;
        }
      }
    }
  }

  for (const fit of out) {
    if (fit.agreement < FIT_MIN) continue;
    const a = regions[fit.from] as TexelRect;
    const b = regions[fit.to] as TexelRect;
    /*
      Reach into the gutter, by exactly the gutter's width.

      Paint is clipped to the regions PLUS their 2px gutters — that is Edge
      Integrity stage 0, done at input time — but no geometry maps to a gutter
      texel, so nothing there ever found a partner. The original stroke was
      therefore continuous across a region boundary and its mirror was not,
      which showed up as a hairline grid cutting the mirrored copy into blocks
      at every face edge.

      `place` is affine in the rect's own coordinates, so a texel just outside
      the source rect maps just outside the target rect on the mirrored side —
      the transform extends into the margin for free. Already-paired texels are
      skipped, so this only ever adds, and the involution sweep below still has
      the final say on anything two regions both reach for.
    */
    /*
      One owner per gutter texel, decided before anything is written.

      Growing each region by the gutter and letting them race left the corners
      contested: two regions both reach a texel diagonally outside them, they
      disagree about where it mirrors to, and the involution sweep then drops
      BOTH — 2,116 texels still unmirrored, which is the dashes at the face
      corners.

      `gutterOwner` resolves it by distance instead, so a texel belongs to the
      region it is actually nearest and the answer does not depend on iteration
      order. Because the assignment is a function of position alone and the
      layout of a region pair is mirror-symmetric, the texel opposite gets the
      opposite owner, and the two directions agree by construction rather than
      by luck.
    */
    for (let y = a.y - GUTTER; y < a.y + a.h + GUTTER; y++) {
      for (let x = a.x - GUTTER; x < a.x + a.w + GUTTER; x++) {
        if (x < 0 || y < 0 || x >= width) continue;
        const i = y * width + x;
        if (i < 0 || i >= partner.length) continue;
        if ((partner[i] ?? -1) >= 0) continue;
        // Inside the region proper, or the nearest region to this gutter texel.
        const own = ownerOf[i] ?? -1;
        if (own >= 0 ? own !== fitIndex.get(fit.from) : gutterOwner[i] !== fitIndex.get(fit.from))
          continue;
        const p = place(fit, a, b, x, y);
        if (p.x < 0 || p.y < 0 || p.x >= width) continue;
        const j = p.y * width + p.x;
        if (j < 0 || j >= partner.length) continue;
        /*
          Fill BOTH directions, and only when both ends are free.

          Setting one side and trusting the opposite region's own fit to agree
          is what left the hairlines. The fit runs per region and geometry wins
          where it exists, so a filled texel i could point at a j that geometry
          had already paired to some j' one texel over. The involution sweep
          then dropped i — about 130 texels per region, which is a whole edge
          of every face, and reads as a line down the middle of a mirrored
          stroke.

          Claiming both ends at once makes the pair self-consistent by
          construction, and refusing when either end is taken means this can
          never overwrite a measured pair.
        */
        if ((partner[j] ?? -1) >= 0) continue;
        link(partner, i, j);
      }
    }
  }

  /*
    Last pass: leave no paintable texel without a mirror.

    Everything above prefers being silent to being wrong — geometry first, then
    a fitted transform, and anything the two disagree about is dropped. That is
    the right instinct for a map, and the wrong outcome for a brush: an
    unpaired texel is a texel the mirror never writes, and a stroke lands on the
    far side with holes in it. Enough of them along an edge and it reads as a
    line drawn through the copy.

    So where a region's transform is trustworthy and a texel STILL has no
    partner, take the transform's answer. It is the same rule already applied to
    that region's other 98%, and being a texel out beats not being there.

    And it applies to the WHOLE region, overwriting the geometric pairs, which
    is the part that took four attempts to see.

    Nearest-neighbour pairing is total but not BIJECTIVE: two source texels can
    choose the same target, and then the target beside it is nobody's partner
    and never gets written. Rendering a mirrored bar made it obvious — a
    one-column gap every 66 texels, which is the region pitch, 64 plus the 2px
    gutter. Not holes in the map. Holes in the IMAGE, from a map that was
    many-to-one.

    A dihedral transform between equal rects is a bijection by construction, so
    using it for the whole region cannot skip a target. It disagrees with
    geometry on under 3% of samples and by about a texel, which is the price:
    the mirror is a texel out in places, instead of striped.
  */
  /*
    ONE fit per region PAIR, applied to both ends.

    This used to run per direction and write one end, and the comment below it
    claimed the disagreements were then dropped — a sweep that was described
    and never written. Both halves of that were wrong, and the map paid for it:
    measured across the shipped rigs, `partner[partner[t]] !== t` for 146 texels
    on the r15-man shirt and 88 on the r15-woman, and on the woman **72 of those
    88 came back in a different face**, up to 261 texels away — most of the
    width of the template.

    The cause is in the sentence above: `a→b` and `b→a` are fitted separately,
    so nothing makes them inverses. Where they disagree, the later one rewrote
    half of a pair and left the other half pointing at its old target. On a limb
    that is not a near miss — the strips are rotated relative to each other, so
    the runner-up dihedral lands a whole face away.

    So the pair is fitted once, in whichever direction scored higher, and both
    ends are written from it. A dihedral transform between equal rects is a
    bijection, so one transform used for both directions is an involution by
    construction rather than by agreement — and there is nothing left to sweep.
  */
  const byPair = new Map<string, RegionFit>();
  for (const fit of out) {
    if (fit.agreement < FIT_MIN) continue;
    // Unordered key, so a→b and b→a compete for the same slot.
    const key = fit.from < fit.to ? `${fit.from}|${fit.to}` : `${fit.to}|${fit.from}`;
    const held = byPair.get(key);
    /*
      Ties broken by name, not by iteration order.

      Two directions of one pair routinely score identically, and picking
      "whichever came first" would make the map depend on region enumeration
      order — a map that changes when an unrelated key moves in the JSON is one
      nobody can reason about.
    */
    if (
      !held ||
      fit.agreement > held.agreement ||
      (fit.agreement === held.agreement && fit.from < held.from)
    ) {
      byPair.set(key, fit);
    }
  }

  for (const fit of byPair.values()) {
    const a = regions[fit.from] as TexelRect;
    const b = regions[fit.to] as TexelRect;
    /*
      The coarse pass engages per PAIR, not per texel.

      "Fill only the gaps" is the obvious reading and it cannot work here: a gap
      is a texel whose partner is taken by somebody else, so filling it either
      leaves the pair one-way — the defect this whole pass was rewritten to
      remove — or refuses and leaves a hole. Measured, refusing per texel traded
      89 wrong pairs on the r15-man shirt for 89 unmirrored ones. Neither is
      acceptable and the choice between them was the wrong question.

      A dihedral transform between equal rects is a bijection, so applying one
      across a whole pair covers every texel on both sides exactly once: no
      holes and no one-way links, by construction. So a pair the fine pass
      covered completely is left alone, and a pair it left ANY gap in is taken
      over wholesale.

      The cost is real and small: that face loses the finer band-by-band fit,
      which follows a tapering limb more closely, and gains the whole-face one,
      which the file already measures as disagreeing by about a texel. A texel
      of drift on a face that needed rescuing beats a hole in every stroke that
      crosses it.
    */
    if (fillOnly && !hasGap(partner, width, a) && !hasGap(partner, width, b)) continue;
    for (let y = a.y - GUTTER; y < a.y + a.h + GUTTER; y++) {
      for (let x = a.x - GUTTER; x < a.x + a.w + GUTTER; x++) {
        if (x < 0 || y < 0 || x >= width) continue;
        const i = y * width + x;
        if (i < 0 || i >= partner.length) continue;
        /*
          Write only what this region owns — the same ownership test the gap
          fill above already uses, and the reason this pass needed it too.

          The sweep covers each region PLUS its gutter ring, so the two-texel
          gutter between neighbours falls inside both of their sweeps. Writing
          it from both meant the second pair silently stole a texel the first
          had already paired, leaving that first partner pointing at nothing
          reciprocal. Exactly 264 of them on every rig — identical body to body
          because it is a fact about the atlas, not about any geometry.

          Resolving ownership once, by distance, means every texel is written by
          one pair and the ambiguity never arises.
        */
        const owner = ownerOf[i] ?? -1;
        const mine = fitIndex.get(fit.from);
        if (owner >= 0 ? owner !== mine : gutterOwner[i] !== mine) continue;
        const q = place(fit, a, b, x, y);
        if (q.x < 0 || q.y < 0 || q.x >= width) continue;
        const j = q.y * width + q.x;
        if (j < 0 || j >= partner.length) continue;
        // Both ends, from one transform: that is what makes it an involution.
        link(partner, i, j);
      }
    }
  }

  /*
    Sweep the direction that lost, for its ring only.

    Fitting each pair once fixed the one-way links and left a new gap: the sweep
    runs over `fit.from`'s rect plus its gutter, so dropping a→b in favour of
    b→a means a's own gutter ring is never visited. Measured, that was the same
    264 texels every time — the two-column margin left of `torso.right`, the
    outermost region in its row, with nothing on its other side to claim it.

    They are outside every face and map to no geometry, so nothing samples them;
    but "the map is total over the area a stroke can reach" is the property this
    file is built on, and quietly exempting a strip of it is how the next real
    hole hides. Fill-only and both-ends-free, so the loser can add pairs and can
    never contradict the winner.
  */
  for (const fit of out) {
    if (fit.agreement < FIT_MIN) continue;
    const a = regions[fit.from] as TexelRect;
    const b = regions[fit.to] as TexelRect;
    for (let y = a.y - GUTTER; y < a.y + a.h + GUTTER; y++) {
      for (let x = a.x - GUTTER; x < a.x + a.w + GUTTER; x++) {
        if (x < 0 || y < 0 || x >= width) continue;
        const i = y * width + x;
        if (i < 0 || i >= partner.length || (partner[i] ?? -1) >= 0) continue;
        const q = place(fit, a, b, x, y);
        if (q.x < 0 || q.y < 0 || q.x >= width) continue;
        const j = q.y * width + q.x;
        if (j < 0 || j >= partner.length || (partner[j] ?? -1) >= 0) continue;
        link(partner, i, j);
      }
    }
  }

  return out;
}

/**
 * Pair two texels, breaking whatever either was paired to first.
 *
 * The fitted passes sweep each region plus its GUTTER ring, so a gutter texel
 * between two neighbours is written twice — once by each side. Assigning both
 * ends directly is still not enough: the second write moves `g` to a new
 * partner and leaves the first one pointing back at it, which is a one-way link
 * made purely by iteration order. Measured, that was 264 of them on every rig,
 * identical body to body because it is a fact about the atlas and not about any
 * geometry, and the worst landed 392 texels from where it should.
 *
 * Unlinking first makes the array a partial involution after every single
 * write, so no ordering can produce a half-pair.
 */
function link(partner: Int32Array, i: number, j: number): void {
  const oldI = partner[i] ?? -1;
  if (oldI >= 0 && oldI !== j && partner[oldI] === i) partner[oldI] = -1;
  const oldJ = partner[j] ?? -1;
  if (oldJ >= 0 && oldJ !== i && partner[oldJ] === j) partner[oldJ] = -1;
  partner[i] = j;
  partner[j] = i;
}

/**
 * Whether any texel inside a region rect still has no partner.
 *
 * The rect proper, without the gutter: gutter texels are shared between
 * neighbours and are legitimately unpaired in plenty of places, so counting
 * them would make every region look like it needed rescuing.
 */
function hasGap(partner: Int32Array, width: number, r: TexelRect): boolean {
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      if ((partner[y * width + x] ?? -1) < 0) return true;
    }
  }
  return false;
}

/** The mirror of a texel, or null where it has none. */
export function mirrorOf(m: MirrorMap, x: number, y: number): { x: number; y: number } | null {
  if (x < 0 || y < 0 || x >= m.width || y >= m.height) return null;
  const j = m.partner[Math.floor(y) * m.width + Math.floor(x)];
  if (j === undefined || j < 0) return null;
  return { x: j % m.width, y: Math.floor(j / m.width) };
}
